import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Youtube, Twitter, Music2, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import useSocialLinks from '../hooks/useSocialLinks';
import { EVENT_CONTENT, EVENT_TYPES } from '../i18n/eventContent';
import { CITIES } from '../data/cities';
import WhatsAppButton from './WhatsAppButton';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_META = [
  { key: 'instagram', icon: Instagram, label: 'Instagram' },
  { key: 'tiktok', icon: Music2, label: 'TikTok' },
  { key: 'facebook', icon: Facebook, label: 'Facebook' },
  { key: 'youtube', icon: Youtube, label: 'YouTube' },
  { key: 'linkedin', icon: Linkedin, label: 'LinkedIn' },
  { key: 'x', icon: Twitter, label: 'X' },
  { key: 'whatsapp', icon: MessageCircle, label: 'WhatsApp' },
];

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t, lang } = useLang();
  const social = useSocialLinks();

  const socialTargets = SOCIAL_META.filter((s) => social[s.key]).map((s) => ({ ...s, href: social[s.key] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 4000);
    } catch (error) {
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <footer className="bg-[#050505] border-t border-white/5 text-gray-400" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center">
                <span className="text-white font-heading font-bold text-sm">P</span>
              </div>
              <span className="font-heading font-bold text-white text-lg tracking-tight">PARTY LED</span>
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed font-body mb-4">
              {t.footer.desc}
            </p>
            <div className="space-y-4 mb-6" data-testid="footer-contacts">
              <div>
                <p className="text-gray-400 text-xs font-body uppercase tracking-wider mb-1.5">
                  <span className="mr-1.5">&#127466;&#127482;</span>Europe
                </p>
                <a href="tel:+33491481213" className="block text-gray-500 text-sm font-body hover:text-purple-400 transition-colors">+33 4 91 48 12 13</a>
                <a href="mailto:info@nouvelle-vision.com" className="block text-gray-500 text-sm font-body hover:text-purple-400 transition-colors">info@nouvelle-vision.com</a>
              </div>
              <div>
                <p className="text-gray-400 text-xs font-body uppercase tracking-wider mb-1.5">
                  <span className="mr-1.5">&#127462;&#127466;</span>UAE
                </p>
                <a href="tel:+971585070177" className="block text-gray-500 text-sm font-body hover:text-purple-400 transition-colors">+971 58 507 0177</a>
                <a href="tel:+971585500626" className="block text-gray-500 text-sm font-body hover:text-purple-400 transition-colors">+971 58 550 0626</a>
                <a href="mailto:info@w-p-p.ae" className="block text-gray-500 text-sm font-body hover:text-purple-400 transition-colors">info@w-p-p.ae</a>
              </div>
            </div>
            <div className="flex gap-3" data-testid="footer-social">
              {socialTargets.length === 0 ? (
                <span className="text-gray-700 text-xs font-body">
                  Réseaux sociaux bientôt disponibles
                </span>
              ) : socialTargets.map(({ key, icon: Icon, label, href }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className="w-9 h-9 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center text-gray-500 hover:text-purple-400 hover:border-purple-500/30 transition-all duration-200"
                  data-testid={`footer-social-${key}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm mb-5 tracking-wide">{t.footer.nav}</h4>
            <ul className="space-y-3">
              {[
                { name: t.nav.home, path: '/' },
                { name: t.nav.kits, path: '/catalogue' },
                { name: t.nav.gallery, path: '/galerie' },
                { name: t.nav.blog, path: '/blog' },
                { name: 'Nos villes', path: '/villes' },
                { name: t.nav.contact, path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm font-body text-gray-500 hover:text-white transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm mb-5 tracking-wide">{t.footer.info}</h4>
            <ul className="space-y-3">
              {[
                { name: t.footer.legal, path: '/mentions-legales' },
                { name: t.footer.privacy, path: '/politique' },
                { name: t.footer.terms, path: '/conditions' },
                { name: t.footer.refund, path: '/remboursement' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm font-body text-gray-500 hover:text-white transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm mb-5 tracking-wide">{t.footer.newsletter}</h4>
            <p className="text-gray-500 text-sm font-body mb-4">
              {t.footer.newsletterDesc}
            </p>
            {subscribed ? (
              <div className="flex items-center gap-2 text-green-400 text-sm font-body" data-testid="newsletter-success">
                <CheckCircle className="w-4 h-4" />
                <span>Inscription réussie !</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.footer.emailPlaceholder}
                  required
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm font-body placeholder:text-gray-600 focus:outline-none focus:border-purple-500/50 transition-all"
                  data-testid="footer-newsletter-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-500 text-white rounded-lg px-3 py-2.5 transition-all duration-200 disabled:opacity-50"
                  data-testid="footer-newsletter-submit"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom — Company Info */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <div className="mb-8" data-testid="footer-cities">
            <p className="text-gray-500 text-xs font-body uppercase tracking-wider mb-3">Bracelets LED par ville</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {CITIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/bracelets-led-${c.slug}`}
                  className="text-xs font-body text-gray-500 hover:text-purple-400 transition-colors duration-200"
                >
                  {c.name}
                </Link>
              ))}
              <Link to="/villes" className="text-xs font-body text-purple-400 hover:text-purple-300 transition-colors duration-200">
                Toutes nos villes →
              </Link>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-8" data-testid="footer-events">
            {EVENT_TYPES.map((type) => ({
              name: (EVENT_CONTENT[type][lang] || EVENT_CONTENT[type].fr).name,
              path: `/evenements/${type}`,
            })).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-xs font-body uppercase tracking-wider text-gray-500 hover:text-purple-400 transition-colors duration-200"
              >
                {link.name}
              </Link>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Europe */}
            <div className="text-left" data-testid="footer-europe-entity">
              <p className="text-gray-500 text-xs font-body uppercase tracking-wider mb-1">
                <span className="mr-1.5">&#127467;&#127479;</span>Livraison Europe
              </p>
              <p className="text-gray-400 text-sm font-body font-semibold">Nouvelle Vision</p>
              <p className="text-gray-500 text-xs font-body">8 Rue Saint Savournin, 13001 Marseille, France</p>
            </div>
            {/* UAE */}
            <div className="text-left md:text-right" data-testid="footer-uae-entity">
              <p className="text-gray-500 text-xs font-body uppercase tracking-wider mb-1">
                <span className="mr-1.5">&#127462;&#127466;</span>UAE Delivery
              </p>
              <p className="text-gray-400 text-sm font-body font-semibold">World Promotional Products</p>
              <p className="text-gray-500 text-xs font-body">Opal Tower, Burj Khalifa Blvd, Office 307/22, Business Bay, Dubai, UAE</p>
            </div>
          </div>
          <div className="text-center">
            <p className="text-gray-600 text-xs font-body tracking-wide">
              © {new Date().getFullYear()} Party LED. {t.footer.rights}
            </p>
          </div>
        </div>
      </div>
    </footer>
    <WhatsAppButton />
    </>
  );
};

export default Footer;
